"use client";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { ArrowUpDown, Calendar, Check, ChevronDown } from "lucide-react";

const DATE_PRESET_VALUES = [
  "today",
  "yesterday",
  "last_7d",
  "last_14d",
  "last_30d",
  "last_90d",
  "this_month",
  "last_month",
  "custom",
] as const;

const COMPARE_PRESET_VALUES = [
  "none",
  "previous_period",
  "last_7d",
  "last_14d",
  "last_30d",
  "last_month",
  "custom",
] as const;

const ATTRIBUTION_MODEL_VALUES = ["data", "first_click", "last_click", "neuroflow"] as const;

const DATE_PRESET_LABEL_KEYS: Record<(typeof DATE_PRESET_VALUES)[number], string> = {
  today: "dashboard.datePresets.today",
  yesterday: "dashboard.datePresets.yesterday",
  last_7d: "dashboard.datePresets.last7d",
  last_14d: "dashboard.datePresets.last14d",
  last_30d: "dashboard.datePresets.last30d",
  last_90d: "dashboard.datePresets.last90d",
  this_month: "dashboard.datePresets.thisMonth",
  last_month: "dashboard.datePresets.lastMonth",
  custom: "dashboard.datePresets.custom",
};

const COMPARE_PRESET_LABEL_KEYS: Record<(typeof COMPARE_PRESET_VALUES)[number], string> = {
  none: "dashboard.comparePresets.none",
  previous_period: "dashboard.comparePresets.previousPeriod",
  last_7d: "dashboard.comparePresets.last7d",
  last_14d: "dashboard.comparePresets.last14d",
  last_30d: "dashboard.comparePresets.last30d",
  last_month: "dashboard.comparePresets.lastMonth",
  custom: "dashboard.comparePresets.custom",
};

const ATTRIBUTION_MODEL_KEYS: Record<
  (typeof ATTRIBUTION_MODEL_VALUES)[number],
  { label: string; desc: string }
> = {
  data: { label: "attribution.models.data.label", desc: "attribution.models.data.desc" },
  first_click: {
    label: "attribution.models.firstClick.label",
    desc: "attribution.models.firstClick.desc",
  },
  last_click: {
    label: "attribution.models.lastClick.label",
    desc: "attribution.models.lastClick.desc",
  },
  neuroflow: {
    label: "attribution.models.neuroflow.label",
    desc: "attribution.models.neuroflow.desc",
  },
};

type DatePreset = (typeof DATE_PRESET_VALUES)[number];
type ComparePreset = (typeof COMPARE_PRESET_VALUES)[number];
type AttributionModel = (typeof ATTRIBUTION_MODEL_VALUES)[number];

interface DashboardControlsMenubarProps {
  datePreset: string;
  onDatePresetChange: (value: string) => void;
  comparePreset: string;
  onComparePresetChange: (value: string) => void;
  attributionModel: AttributionModel;
  onAttributionModelChange: (value: AttributionModel) => void;
  isAllegroMode?: boolean;
}

export default function DashboardControlsMenubar({
  datePreset,
  onDatePresetChange,
  comparePreset,
  onComparePresetChange,
  attributionModel,
  onAttributionModelChange,
  isAllegroMode = false,
}: DashboardControlsMenubarProps) {
  const { t } = useTranslation();

  const datePresets = useMemo(
    () => DATE_PRESET_VALUES.map((value) => ({ value, label: t(DATE_PRESET_LABEL_KEYS[value]) })),
    [t]
  );
  const comparePresets = useMemo(
    () =>
      COMPARE_PRESET_VALUES.map((value) => ({ value, label: t(COMPARE_PRESET_LABEL_KEYS[value]) })),
    [t]
  );
  const attributionModels = useMemo(
    () =>
      ATTRIBUTION_MODEL_VALUES.map((value) => ({
        value,
        label: t(ATTRIBUTION_MODEL_KEYS[value].label),
        desc: t(ATTRIBUTION_MODEL_KEYS[value].desc),
      })),
    [t]
  );

  const selectedDateLabel =
    datePresets.find((p) => p.value === datePreset)?.label ?? t("dashboardMenu.period");
  const selectedCompareLabel =
    comparePresets.find((p) => p.value === comparePreset)?.label ?? t("dashboardMenu.compare");
  const selectedAttributionLabel =
    attributionModels.find((m) => m.value === attributionModel)?.label ??
    t("attribution.defaultLabel");

  return (
    <Menubar className="h-auto border-border bg-muted p-0.5 gap-0.5">
      {/* Date period */}
      <MenubarMenu>
        <MenubarTrigger className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-foreground hover:text-foreground bg-accent hover:bg-accent data-[state=open]:bg-accent data-[state=open]:text-foreground cursor-pointer rounded-sm">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          {selectedDateLabel}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </MenubarTrigger>
        <MenubarContent className="bg-card border-border min-w-[12rem]">
          <MenubarSub>
            <MenubarSubTrigger className="text-muted-foreground text-xs">
              {t("dashboardMenu.quickRanges")}
            </MenubarSubTrigger>
            <MenubarSubContent className="bg-card border-border">
              {datePresets
                .filter((p) => ["today", "yesterday", "last_7d", "last_14d"].includes(p.value))
                .map((p) => (
                  <MenubarItem
                    key={p.value}
                    onClick={() => onDatePresetChange(p.value)}
                    className="text-sm text-foreground hover:text-foreground focus:text-foreground focus:bg-accent cursor-pointer"
                  >
                    {datePreset === p.value && (
                      <Check className="w-3.5 h-3.5 mr-2 text-[#ef3e29]" />
                    )}
                    {p.label}
                  </MenubarItem>
                ))}
            </MenubarSubContent>
          </MenubarSub>
          <MenubarSub>
            <MenubarSubTrigger className="text-muted-foreground text-xs">
              {t("dashboardMenu.longerRanges")}
            </MenubarSubTrigger>
            <MenubarSubContent className="bg-card border-border">
              {datePresets
                .filter((p) =>
                  ["last_30d", "last_90d", "this_month", "last_month"].includes(p.value)
                )
                .map((p) => (
                  <MenubarItem
                    key={p.value}
                    onClick={() => onDatePresetChange(p.value)}
                    className="text-sm text-foreground hover:text-foreground focus:text-foreground focus:bg-accent cursor-pointer"
                  >
                    {datePreset === p.value && (
                      <Check className="w-3.5 h-3.5 mr-2 text-[#ef3e29]" />
                    )}
                    {p.label}
                  </MenubarItem>
                ))}
            </MenubarSubContent>
          </MenubarSub>
          <MenubarSeparator className="bg-accent" />
          {datePresets.map((p) =>
            p.value === "custom" ? (
              <MenubarItem
                key={p.value}
                onClick={() => onDatePresetChange(p.value)}
                className="text-sm text-foreground hover:text-foreground focus:text-foreground focus:bg-accent cursor-pointer"
              >
                {datePreset === p.value && <Check className="w-3.5 h-3.5 mr-2 text-[#ef3e29]" />}
                {p.label}
              </MenubarItem>
            ) : null
          )}
        </MenubarContent>
      </MenubarMenu>

      {/* Comparison period */}
      <MenubarMenu>
        <MenubarTrigger className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-foreground hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground cursor-pointer rounded-sm">
          <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
          {selectedCompareLabel}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </MenubarTrigger>
        <MenubarContent className="bg-card border-border min-w-[13rem]">
          {comparePresets.map((p) => (
            <MenubarItem
              key={p.value}
              onClick={() => onComparePresetChange(p.value)}
              className="text-sm text-foreground hover:text-foreground focus:text-foreground focus:bg-accent cursor-pointer"
            >
              {comparePreset === p.value && <Check className="w-3.5 h-3.5 mr-2 text-[#ef3e29]" />}
              {p.label}
            </MenubarItem>
          ))}
        </MenubarContent>
      </MenubarMenu>

      {/* Attribution model — hidden in Allegro mode */}
      {!isAllegroMode && (
        <MenubarMenu>
          <MenubarTrigger className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-foreground hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground cursor-pointer rounded-sm">
            {selectedAttributionLabel}
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          </MenubarTrigger>
          <MenubarContent className="bg-card border-border min-w-[14rem]">
            {attributionModels.map((m) => (
              <MenubarItem
                key={m.value}
                onClick={() => onAttributionModelChange(m.value)}
                className="text-sm text-foreground hover:text-foreground focus:text-foreground focus:bg-accent cursor-pointer"
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 w-3.5 h-3.5 shrink-0">
                    {attributionModel === m.value && (
                      <Check className="w-3.5 h-3.5 text-[#ef3e29]" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium">{m.label}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{m.desc}</div>
                  </div>
                </div>
              </MenubarItem>
            ))}
          </MenubarContent>
        </MenubarMenu>
      )}
    </Menubar>
  );
}
